import React, { useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { PollVotePayload } from '../PollView/redux/actionCreators';
import PollOption from './PollOption';

import localStyles from './PollView.scss';

export interface Props {
  deviation: PapiDeviation;
  canVote: boolean;
  className?: string;
  answerClassName?: string;
  isMobile?: boolean;
  hideQuestion?: boolean;
  draftJsThemeOverride?: any;
  stylesheet?: Record<string, string>;
  optionStylesheet?: Record<string, string>;
  optionImageSize?: number;
  optionHoverImageSize?: number;
  onVote: (params: PollVotePayload) => void;
}

const PollView: React.FC<React.PropsWithChildren<Props>> = ({
  deviation: { poll },
  onVote,
  canVote,
  className,
  hideQuestion,
  draftJsThemeOverride,
  answerClassName,
  stylesheet: s = localStyles,
  optionStylesheet,
  optionImageSize,
  optionHoverImageSize,
}) => {
  const { t } = useTranslation();
  const onClickOption = useCallback(
    (answerId: number) => {
      if (!poll) {
        return;
      }
      onVote({ pollId: poll.pollId, userId: poll.user.userId, answerId });
    },
    [onVote, poll]
  );

  if (!poll) {
    return null;
  }

  return (
    <BiLoggerContextProvider value={{ pollid: poll.pollId }}>
      <div className={classnames(s['root'], className)}>
        {!hideQuestion && (
          <>
            <div className={s['question']}>{poll.question}</div>
            <div className={s['info']}>
              <span className={s['votes']}>
                {t('pollView.votes', { count: poll.votes })}
              </span>
            </div>
          </>
        )}
        {poll.answers.map(answer => (
          <PollOption
            key={answer.answerId}
            className={s['poll-option']}
            answer={answer}
            canVote={canVote}
            onClick={onClickOption}
            draftJsThemeOverride={draftJsThemeOverride}
            answerClassName={answerClassName}
            stylesheet={optionStylesheet}
            imageSize={optionImageSize}
            hoverImageSize={optionHoverImageSize}
          />
        ))}
      </div>
    </BiLoggerContextProvider>
  );
};

PollView.displayName = 'PollView';

export default PollView;
